import React, { useState, useEffect } from "react";
import Layout from "../../Component/Layout";
import Select from "react-select";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { PURCHASE_API, MASTER_API } from "../../apiConfig";
import { Link, useLocation } from "react-router-dom";
import { useFieldPermissions } from "../../utils/useFieldPermissions";
import MaskedValue from "../../Component/MaskedValue";

// Reads module-level permissions (can_view / can_create / can_edit /
// can_convert) for SFC_REWORK_ORDER from the logged-in user's stored
// permissions — same array authController.js returns at login, same
// pattern Confirmation.js / GoodsRequest.js use. Admins get an empty
// `permissions` array from the backend by design, so they're granted full
// access here rather than being locked out by an "empty permissions"
// default.
//
// Rework Order only ever needs two of the four flags:
//  - canView    -> DISPLAY: browse/view both tabs (Active, Posted).
//  - canConvert -> CONVERT: on the Active tab, turn a selected rejected
//                  batch into a Rework Order (the "Create Rework" button).
// There is no separate Edit/Change action here, so canCreate/canEdit are
// read but intentionally unused.
const getModulePermissions = (moduleId) => {
    try {
        const user = JSON.parse(localStorage.getItem("user"));
        if (!user) return { canView: false, canCreate: false, canEdit: false, canConvert: false };
        if (user.role === "admin") return { canView: true, canCreate: true, canEdit: true, canConvert: true };

        const perm = (user.permissions || []).find((p) => p.module_id === moduleId);
        return {
            canView: perm?.can_view === 1,
            canCreate: perm?.can_create === 1,
            canEdit: perm?.can_edit === 1,
            canConvert: perm?.can_convert === 1
        };
    } catch (e) {
        console.error("Error reading user permissions:", e);
        return { canView: false, canCreate: false, canEdit: false, canConvert: false };
    }
};

const selectStyles = {
    control: (base, state) => ({
        ...base, minHeight: 32, height: 32, fontSize: 12,
        borderColor: state.isFocused ? "#64748b" : "#e2e8f0",
        borderRadius: 6, boxShadow: "none", cursor: "pointer",
        "&:hover": { borderColor: "#94a3b8" },
    }),
    valueContainer: (base) => ({ ...base, padding: "0 8px", height: 32, flexWrap: "nowrap" }),
    input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: 12 }),
    indicatorsContainer: (base) => ({ ...base, height: 32 }),
    dropdownIndicator: (base) => ({ ...base, padding: "0 4px", color: "#94a3b8", svg: { width: 12, height: 12 } }),
    clearIndicator: (base) => ({ ...base, padding: "0 2px", color: "#94a3b8", svg: { width: 12, height: 12 } }),
    indicatorSeparator: () => ({ display: "none" }),
    placeholder: (base) => ({ ...base, color: "#94a3b8", fontSize: 12 }),
    singleValue: (base) => ({ ...base, fontSize: 12, color: "#1e293b" }),
    menu: (base) => ({ ...base, zIndex: 9999, fontSize: 12, borderRadius: 6, boxShadow: "0 4px 12px rgba(0,0,0,0.12)" }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    option: (base, state) => ({
        ...base, fontSize: 12, padding: "6px 10px",
        backgroundColor: state.isSelected ? "#1e293b" : state.isFocused ? "#f1f5f9" : "#fff",
        color: state.isSelected ? "#fff" : "#334155",
    }),
};

const styles = {
    tableContainer: { background: "#ffffff", borderRadius: 8, padding: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.08)", overflowX: "auto", WebkitOverflowScrolling: "touch" },
    erpTable: { width: "100%", minWidth: "980px", tableLayout: "fixed", borderCollapse: "collapse" },
    erpTh: { background: "#f8fafc", fontSize: 13, fontWeight: 600, padding: "10px 12px", borderBottom: "1px solid #e5e7eb", color: "#1e293b", whiteSpace: "nowrap" },
    erpTd: { fontSize: 13, padding: "10px 12px", borderBottom: "1px solid #f1f5f9", color: "#334155", verticalAlign: "middle" },
    filterLabel: { fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 },
    dateInput: { width: "100%", height: 32, fontSize: 12, border: "1px solid #e2e8f0", borderRadius: 6, padding: "0 8px", color: "#1e293b", background: "#fff", boxSizing: "border-box", cursor: "pointer" },
};

const getCurrentUser = () => {
    const userObj = localStorage.getItem("user");
    if (userObj) {
        try {
            const parsedUser = JSON.parse(userObj);
            const fullName = `${parsedUser.firstName || ''} ${parsedUser.lastName || ''}`.trim();
            if (fullName) return fullName;
            if (parsedUser.email) return parsedUser.email;
        } catch (e) { console.error("Error parsing user data:", e); }
    }
    const email = localStorage.getItem("email");
    if (email && email !== "undefined" && email !== "null") return email;
    const storedName = localStorage.getItem("userName");
    if (storedName && storedName !== "undefined" && storedName !== "null") return storedName;
    return "User";
};

const ReworkOrder = () => {
    const location = useLocation();

    // Module-level access for this page — see getModulePermissions above.
    const { canView, canConvert } = React.useMemo(
        () => getModulePermissions("SFC_REWORK_ORDER"),
        []
    );
    const canSeePage = canView || canConvert;

    // Field-level access for this module. Falls open (true) for admins,
    // users with no role assigned yet, or any non-DISPLAY permission type —
    // matching useFieldPermissions' own documented behavior.
    const { isFieldVisible } = useFieldPermissions("SFC_REWORK_ORDER");
    
    const [rejectedBatches, setRejectedBatches] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);
    const [activeTab, setActiveTab] = useState("active");

    const [selectedProductType, setSelectedProductType] = useState(null);
    const [listingFilters, setListingFilters] = useState({
        construction: null, code: null, cableType: null, category: null,
        noOfCores: null, color: null, nominalCrossSection: null,
        referenceOfStandard: null, voltage: null,
    });
    const [selectedProductionNo, setSelectedProductionNo] = useState(null);
    const [selectedProductCode, setSelectedProductCode] = useState(null);
    const [selectedWorkcenter, setSelectedWorkcenter] = useState(null);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    const [productTypeOptions, setProductTypeOptions] = useState([]);
    const [constructionOptions, setConstructionOptions] = useState([]);
    const [codeOptions, setCodeOptions] = useState([]);
    const [cableTypeOptions, setCableTypeOptions] = useState([]);
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [coresOptions, setCoresOptions] = useState([]);
    const [colorOptions, setColorOptions] = useState([]);
    const [crossSectionOptions, setCrossSectionOptions] = useState([]);
    const [stdRefOptions, setStdRefOptions] = useState([]);
    const [voltageOptions, setVoltageOptions] = useState([]);
    const [productionOrderOptions, setProductionOrderOptions] = useState([]);
    const [productCodeOptions, setProductCodeOptions] = useState([]);
    const [workcenterOptions, setWorkcenterOptions] = useState([]);

    const handleListingFilter = (key, opt) => {
        setListingFilters((prev) => ({ ...prev, [key]: opt }));
    };

    const getFilterOptions = (list) => {
        if (!list || !Array.isArray(list)) return [];
        return list.map((item) => {
            if (typeof item === "string") return { value: item, label: item };
            const val = item.name || item.code || item.cable_type || item.category || item.core || item.colour || item.value || "";
            return { value: String(val).trim(), label: String(val).trim() };
        }).filter((opt) => opt.value !== "");
    };

    useEffect(() => {
        const fetchFilterMasterData = async () => {
            try {
                const [
                    resProdTypes, resCons, resCode, resCable, resCat,
                    resCore, resCol, resCross, resRef, resVolt,
                ] = await Promise.all([
                    fetch(`${MASTER_API}/configuration/product-types`),
                    fetch(`${MASTER_API}/configuration/construction`),
                    fetch(`${MASTER_API}/configuration/dynamic/code`),
                    fetch(`${MASTER_API}/configuration/cable-type`),
                    fetch(`${MASTER_API}/configuration/category`),
                    fetch(`${MASTER_API}/configuration/core`),
                    fetch(`${MASTER_API}/configuration/colour`),
                    fetch(`${MASTER_API}/configuration/dynamic/nominal-cross-section`),
                    fetch(`${MASTER_API}/configuration/dynamic/reference-of-standard`),
                    fetch(`${MASTER_API}/configuration/dynamic/voltage`),
                ]);

                const prodTypesData = resProdTypes.ok ? await resProdTypes.json() : [];
                setProductTypeOptions(prodTypesData.map((item) => ({ value: item.name, label: item.name })).filter((o) => o.value));
                setConstructionOptions(getFilterOptions(resCons.ok ? await resCons.json() : []));
                setCodeOptions(getFilterOptions(resCode.ok ? await resCode.json() : []));
                setCableTypeOptions(getFilterOptions(resCable.ok ? await resCable.json() : []));
                setCategoryOptions(getFilterOptions(resCat.ok ? await resCat.json() : []));
                setCoresOptions(getFilterOptions(resCore.ok ? await resCore.json() : []));
                setColorOptions(getFilterOptions(resCol.ok ? await resCol.json() : []));
                setCrossSectionOptions(getFilterOptions(resCross.ok ? await resCross.json() : []));
                setStdRefOptions(getFilterOptions(resRef.ok ? await resRef.json() : []));
                setVoltageOptions(getFilterOptions(resVolt.ok ? await resVolt.json() : []));
            } catch (err) {
                console.error("Failed fetching rework filter master data", err);
            }
        };

        fetchFilterMasterData();

        axios.get(`${MASTER_API}/workcenter`)
            .then((res) => setWorkcenterOptions((res.data.data || res.data).map((item) => ({ value: item.workcenter_name, label: item.workcenter_name }))))
            .catch(console.error);

        axios.get(`${PURCHASE_API}/production-order/order-numbers`)
            .then((res) => setProductionOrderOptions((res.data || []).map((p) => ({ value: p.production_order_number, label: p.production_order_number }))))
            .catch(console.error);

        axios.get(`${PURCHASE_API}/production-order/product-codes`)
            .then((res) => {
                const data = res.data || [];
                const options = Array.from(new Map(data.map((item) => {
                    const code = String(item.product_code || item.productCode || "").trim();
                    const desc = String(item.productDescription || item.description || item.product_description || "").trim();
                    return [code, { value: code, label: desc ? `${code} — ${desc}` : code }];
                })).values()).filter((opt) => opt.value !== "");
                setProductCodeOptions(options);
            }).catch(console.error);
    }, []);

    const loadRejectedBatches = async () => {
        setLoading(true);
        try {
            const params = {};
            if (selectedWorkcenter?.value) params.workcenter = selectedWorkcenter.value;

            const res = await axios.get(`${PURCHASE_API}/rework-order/list`, { params });
            const data = res.data || [];

            const mapped = data.map((row) => ({
                id: row.id,
                productionOrder: row.production_order_number,
                batch: row.batch_number,
                productCode: row.product_code,
                description: row.description,
                workcenter: row.workcenter_name,
                orderQty: row.order_qty,
                startDate: row.start_date,
                endDate: row.end_date,
                reworkOrderNo: row.rework_order_no,
                isConverted: !!row.is_converted
            }));

            setRejectedBatches(mapped);
        } catch (err) {
            console.error("Failed fetching rework list", err);
            setRejectedBatches([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadRejectedBatches();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedWorkcenter]);

    useEffect(() => {
        setSelectedRows([]);
    }, [activeTab]);

    const toggleRow = (id) => {
        setSelectedRows((prev) => (prev.includes(id) ? [] : [id]));
    };

    const [creating, setCreating] = useState(false);

    const handleCreateRework = async (batchNumber) => {
        if (!batchNumber) return;
        setCreating(true);
        try {
            const res = await axios.post(`${PURCHASE_API}/rework-order/create`, {
                batchNumber,
                createdBy: getCurrentUser(),
            });

            if (res.data?.success) {
                toast.success(`Rework Order ${res.data.reworkOrderNo} created successfully!`);
                setSelectedRows([]);
                loadRejectedBatches();
            }
        } catch (err) {
            console.error("Failed creating rework order", err);
            toast.error(err.response?.data?.error || "Failed to create rework order");
        } finally {
            setCreating(false);
        }
    };

    const tabFilteredBatches = rejectedBatches.filter(o => {
        if (activeTab === "active") return !o.isConverted;
        if (activeTab === "posted") return !!o.isConverted;
        return true;
    });

    const activeCount = rejectedBatches.filter(o => !o.isConverted).length;
    const postedCount = rejectedBatches.filter(o => !!o.isConverted).length;

    // A non-admin user with no DISPLAY/CONVERT entry for this module
    // (either no role assigned at all, or a role that simply doesn't
    // include Rework Order) has canView/canConvert both false, per
    // getModulePermissions above — same convention Confirmation and
    // Goods Request use. Admins always pass via the explicit
    // user.role === "admin" branch.
    if (!canSeePage) {
        return (
            <Layout>
                <div style={{ padding: 40, textAlign: "center", color: "#64748b", fontSize: 14 }}>
                    You don't have access to Rework Order. Contact your administrator if you believe this is a mistake.
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <style>{`
                .po-tab-toggle { display: inline-flex; background: #f1f5f9; border-radius: 8px; padding: 3px; gap: 2px; }
                .po-tab-toggle button {
                    border: none; background: transparent; border-radius: 6px;
                    padding: 5px 20px; font-size: 13px; font-weight: 600;
                    color: #64748b; cursor: pointer; transition: all 0.15s ease;
                    display: flex; align-items: center; gap: 6px;
                }
                .po-tab-toggle button.po-active-tab {
                    background: #fff; color: #1e293b; box-shadow: 0 1px 3px rgba(0,0,0,0.12);
                }
                .po-badge {
                    display: inline-flex; align-items: center; justify-content: center;
                    min-width: 18px; height: 18px; border-radius: 9px;
                    font-size: 11px; font-weight: 700; padding: 0 5px; line-height: 1;
                }
                .po-badge-active { background: #dbeafe; color: #1d4ed8; }
                .po-badge-posted { background: #dcfce7; color: #15803d; }
            `}</style>

            <div style={{ padding: "20px 24px", maxWidth: "100%", boxSizing: "border-box" }}>
                {/* Page Header */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 20, background: "#fff", borderRadius: 10, padding: "16px 20px", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}>
                    <div>
                        <h2 style={{ fontSize: 22, fontWeight: 700, color: "#1e293b", margin: 0 }}>Rework Order</h2>
                        <div style={{ fontSize: 13, color: "#64748b", marginTop: 3 }}>Create and manage rework orders for rejected batches</div>
                    </div>
                    {activeTab === "active" && canConvert && (
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <button
                                disabled={selectedRows.length !== 1 || creating}
                                onClick={() => {
                                    const row = rejectedBatches.find((r) => (r.id ?? r.batch) === selectedRows[0]);
                                    if (!row) return;
                                    handleCreateRework(row.batch);
                                }}
                                style={{
                                    opacity: selectedRows.length !== 1 || creating ? 0.5 : 1,
                                    cursor: selectedRows.length !== 1 || creating ? "not-allowed" : "pointer",
                                    padding: "9px 20px", background: "#1e293b", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, display: "flex", alignItems: "center", gap: 8, boxShadow: "0 2px 8px rgba(0,0,0,0.18)", whiteSpace: "nowrap", transition: "background 0.2s",
                                }}
                                onMouseEnter={(e) => { if (selectedRows.length === 1 && !creating) e.currentTarget.style.background = "#334155" }}
                                onMouseLeave={(e) => (e.currentTarget.style.background = "#1e293b")}
                            >
                                <span style={{ fontSize: 20, lineHeight: 1, fontWeight: 400 }}>+</span>
                                {creating ? "Creating..." : "Create Rework"}
                            </button>
                        </div>
                    )}
                </div>

                {/* FILTERS */}
                <div style={{ background: "#fff", borderRadius: 10, padding: "14px 20px", boxShadow: "0 1px 4px rgba(0,0,0,0.08)", marginBottom: 20 }}>
                    <div className="row mb-3">
                        <div className="col-md-3">
                            <label style={styles.filterLabel}>Product Type</label>
                            <Select options={productTypeOptions} value={selectedProductType} onChange={(opt) => setSelectedProductType(opt)} isClearable placeholder="Select Product Type" menuPortalTarget={document.body} menuPosition="fixed" styles={selectStyles} />
                        </div>
                    </div>
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 10 }}>
                        {[
                            { key: "construction", opts: constructionOptions, label: "Construction" },
                            { key: "code", opts: codeOptions, label: "Code" },
                            { key: "cableType", opts: cableTypeOptions, label: "Cable Type" },
                            { key: "category", opts: categoryOptions, label: "Category" },
                            { key: "noOfCores", opts: coresOptions, label: "Cores" },
                            { key: "color", opts: colorOptions, label: "Color" },
                            { key: "nominalCrossSection", opts: crossSectionOptions, label: "Cross Section" },
                            { key: "referenceOfStandard", opts: stdRefOptions, label: "Std. Ref" },
                            { key: "voltage", opts: voltageOptions, label: "Voltage" },
                        ].map(({ key, opts, label }) => (
                            <div key={key} style={{ flex: "1 1 0", minWidth: 100 }}>
                                <label style={styles.filterLabel}>{label}</label>
                                <Select placeholder={label} isClearable options={opts} value={listingFilters[key]} onChange={(opt) => handleListingFilter(key, opt)} menuPortalTarget={document.body} menuPosition="fixed" styles={selectStyles} />
                            </div>
                        ))}
                    </div>
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
                        <div style={{ flex: "1 1 0", minWidth: 150 }}>
                            <label style={styles.filterLabel}>Production Order</label>
                            <Select options={productionOrderOptions} value={selectedProductionNo} onChange={(opt) => setSelectedProductionNo(opt)} isClearable placeholder="Production Order" menuPortalTarget={document.body} menuPosition="fixed" styles={selectStyles} />
                        </div>
                        <div style={{ flex: "1 1 0", minWidth: 150 }}>
                            <label style={styles.filterLabel}>Product Code</label>
                            <Select options={productCodeOptions} value={selectedProductCode} onChange={(opt) => setSelectedProductCode(opt)} isClearable placeholder="Product Code" menuPortalTarget={document.body} menuPosition="fixed" styles={selectStyles} />
                        </div>
                        <div style={{ flex: "1 1 0", minWidth: 150 }}>
                            <label style={styles.filterLabel}>Workcenter</label>
                            <Select options={workcenterOptions} value={selectedWorkcenter} onChange={(opt) => setSelectedWorkcenter(opt)} isClearable placeholder="Workcenter" menuPortalTarget={document.body} menuPosition="fixed" styles={selectStyles} />
                        </div>
                        <div style={{ flex: "1 1 0", minWidth: 150 }}>
                            <label style={styles.filterLabel}>From</label>
                            <input type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); if (!e.target.value) setEndDate(""); }} style={styles.dateInput} />
                        </div>
                        <div style={{ flex: "1 1 0", minWidth: 150 }}>
                            <label style={styles.filterLabel}>To</label>
                            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} disabled={!startDate} style={{ ...styles.dateInput, color: !startDate ? "#94a3b8" : "#1e293b", background: !startDate ? "#f1f5f9" : "#fff", cursor: !startDate ? "not-allowed" : "pointer" }} />
                        </div>
                    </div>
                </div>

                {/* ── Active / Posted Toggle ── */}
                <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 14 }}>
                    <div className="po-tab-toggle">
                        <button className={activeTab === "active" ? "po-active-tab" : ""} onClick={() => setActiveTab("active")}>
                            Active
                            {activeCount > 0 && <span className="po-badge po-badge-active">{activeCount}</span>}
                        </button>
                        <button className={activeTab === "posted" ? "po-active-tab" : ""} onClick={() => setActiveTab("posted")}>
                            Posted
                            {postedCount > 0 && <span className="po-badge po-badge-posted">{postedCount}</span>}
                        </button>
                    </div>
                </div>

                {/* ===================== TABLE ===================== */}
                <div style={styles.tableContainer}>
                    <table style={styles.erpTable}>
                        <thead>
                            <tr>
                                {activeTab === "active" && canConvert && <th style={{ ...styles.erpTh, width: 40 }}></th>}
                                {activeTab === "posted" && <th style={styles.erpTh}>Rework Order</th>}
                                <th style={styles.erpTh}>Production Order</th>
                                <th style={styles.erpTh}>Batch Number</th>
                                <th style={styles.erpTh}>Product Code</th>
                                <th style={styles.erpTh}>Description</th>
                                <th style={styles.erpTh}>Workcenter</th>
                                <th style={{...styles.erpTh, textAlign: "right"}}>Order Qty</th>
                                <th style={styles.erpTh}>Start Date</th>
                                <th style={styles.erpTh}>End Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td style={styles.erpTd} colSpan="10" align="center">Loading...</td>
                                </tr>
                            ) : tabFilteredBatches.length === 0 ? (
                                <tr>
                                    <td style={styles.erpTd} colSpan="10" align="center">
                                        {activeTab === "active" ? "No rejected batches found" : "No converted rework orders found"}
                                    </td>
                                </tr>
                            ) : (
                                tabFilteredBatches.map((row, idx) => {
                                    const rowId = row.id ?? row.batch ?? idx;
                                    const isSelected = selectedRows.includes(rowId);
                                    
                                    return (
                                        <tr key={rowId} style={{ background: isSelected ? "#eff6ff" : (idx % 2 === 0 ? "#fff" : "#fafbfc") }}
                                            onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = "#f1f5f9"; }}
                                            onMouseLeave={e => { e.currentTarget.style.background = isSelected ? "#eff6ff" : (idx % 2 === 0 ? "#fff" : "#fafbfc"); }}>
                                            
                                            {activeTab === "active" && canConvert && (
                                                <td style={styles.erpTd}>
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={() => toggleRow(rowId)}
                                                        style={{ cursor: "pointer", width: 14, height: 14 }}
                                                    />
                                                </td>
                                            )}
                                            
                                            {activeTab === "posted" && (
                                                <td style={{ ...styles.erpTd, fontWeight: 700, color: "#2563eb" }}>
                                                    {!isFieldVisible('reworkOrderNo') ? <MaskedValue /> : (
                                                        row.reworkOrderNo ? (
                                                            <Link
                                                                to={`/cable/production-order?pod=${encodeURIComponent(row.reworkOrderNo)}&returnTo=${encodeURIComponent(location.pathname + location.search)}`}
                                                                style={{ color: "#2563eb", textDecoration: "underline", textUnderlineOffset: 3 }}
                                                                onMouseEnter={e => e.currentTarget.style.color = "#1d4ed8"}
                                                                onMouseLeave={e => e.currentTarget.style.color = "#2563eb"}
                                                            >
                                                                {row.reworkOrderNo}
                                                            </Link>
                                                        ) : ""
                                                    )}
                                                </td>
                                            )}
                                            
                                            <td style={{ ...styles.erpTd, fontWeight: 600 }}>
                                                {!isFieldVisible('productionOrder') ? <MaskedValue /> : (
                                                    row.productionOrder ? (
                                                        <Link
                                                            to={`/cable/production-order?pod=${encodeURIComponent(row.productionOrder)}&returnTo=${encodeURIComponent(location.pathname + location.search)}`}
                                                            style={{ color: "#2563eb", textDecoration: "underline", textUnderlineOffset: 3 }}
                                                            onMouseEnter={e => e.currentTarget.style.color = "#1d4ed8"}
                                                            onMouseLeave={e => e.currentTarget.style.color = "#2563eb"}
                                                        >
                                                            {row.productionOrder}
                                                        </Link>
                                                    ) : ""
                                                )}
                                            </td>

                                            <td style={styles.erpTd}>
                                                {!isFieldVisible('batch') ? <MaskedValue /> : row.batch}
                                            </td>

                                            <td style={{ ...styles.erpTd, fontWeight: 500 }}>
                                                {!isFieldVisible('productCode') ? <MaskedValue /> : (
                                                    row.productCode ? (
                                                        <Link
                                                            to={`/cable/product/product-code?view=${encodeURIComponent(row.productCode)}&returnTo=${encodeURIComponent(location.pathname + location.search)}`}
                                                            style={{ color: "#2563eb", textDecoration: "underline", textUnderlineOffset: 3 }}
                                                            onMouseEnter={e => e.currentTarget.style.color = "#1d4ed8"}
                                                            onMouseLeave={e => e.currentTarget.style.color = "#2563eb"}
                                                        >
                                                            {row.productCode}
                                                        </Link>
                                                    ) : ""
                                                )}
                                            </td>

                                            <td style={{ ...styles.erpTd, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={row.description}>
                                                {!isFieldVisible('description') ? <MaskedValue /> : row.description}
                                            </td>
                                            <td style={styles.erpTd}>
                                                {!isFieldVisible('workcenter') ? <MaskedValue /> : row.workcenter}
                                            </td>
                                            <td style={{ ...styles.erpTd, textAlign: "right", fontWeight: 600 }}>
                                                {!isFieldVisible('orderQty') ? <MaskedValue /> : row.orderQty}
                                            </td>
                                            <td style={styles.erpTd}>
                                                {!isFieldVisible('startDate') ? <MaskedValue /> : row.startDate}
                                            </td>
                                            <td style={styles.erpTd}>
                                                {!isFieldVisible('endDate') ? <MaskedValue /> : row.endDate}
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
            </div>
        </Layout>
    );
};

export default ReworkOrder;